import styled from 'styled-components'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { BodySmallMedium, Title2, BodyLarge } from '../../../components/styles/Typography'
import { theme } from '../../../theme/theme'
import { useTranslation } from 'react-i18next'
import { Arrow, Send } from '../../../components/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useCreateCaseMutation } from '../../../api/ester-b2b-api/private/portalCase/portalCaseQueries'

const DEFAULT_BACK = '/portal/messages'

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const getBackTarget = (state: unknown): string => {
  if (isRecord(state) && typeof state.from === 'string' && state.from.startsWith('/portal/')) {
    return state.from
  }
  return DEFAULT_BACK
}

const isBookingRequest = (state: unknown): boolean => isRecord(state) && state.bookingRequest === true

const getInitialMessage = (state: unknown): string =>
  isRecord(state) && typeof state.initialMessage === 'string' ? state.initialMessage : ''

export const NewConversation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [message, setMessage] = useState(() =>
    isBookingRequest(location.state) ? t('employee.messages.bookingRequestTemplate') : getInitialMessage(location.state)
  )
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: createCase, isPending } = useCreateCaseMutation()

  const isFormValid = message.trim() !== ''

  const backTarget = getBackTarget(location.state)

  const handleBack = () => {
    navigate(backTarget)
  }

  const handleSend = async () => {
    if (!isFormValid || isPending) return

    setError(null)
    try {
      const newCase = await createCase({ message })
      navigate(`/portal/messages/${newCase.id}`)
    } catch {
      setError(t('employee.messages.createError'))
    }
  }

  return (
    <>
      <ComponentLayout
        paddingTop={theme.spacing.xxlarge}
        paddingBottom={theme.spacing.small}
        paddingLeft={theme.spacing.large}
        paddingRight={theme.spacing.large}
      >
        <HeaderContent>
          <Title2>{t('employee.messages.title')}</Title2>
        </HeaderContent>
      </ComponentLayout>

      <SubHeader>
        <BackButton onClick={handleBack}>
          <Arrow size={24} color={theme.color.black} left />
        </BackButton>
      </SubHeader>

      <FormContainer>
        <FormContent>
          <InputGroup>
            <InputLabel>{t('employee.messages.messageLabel')}</InputLabel>
            <MessageTextarea
              placeholder={t('employee.messages.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </InputGroup>

          <SubmitSection>
            <SendButton onClick={handleSend} $disabled={!isFormValid || isPending}>
              <Send size={16} color={isFormValid && !isPending ? theme.color.white : theme.color.grey4} />
              <span>{isPending ? t('common.form.loading') : t('employee.messages.sendButton')}</span>
            </SendButton>
            <HelperText>{t('employee.messages.replyTime')}</HelperText>
            {error && <ErrorText>{error}</ErrorText>}
          </SubmitSection>
        </FormContent>
      </FormContainer>
    </>
  )
}

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.mediumLarge}px;
`

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding: ${theme.spacing.small}px ${theme.spacing.large}px;
  border-bottom: 1px solid ${theme.color.beigeDark};
  background-color: ${theme.color.beigeLight};
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.color.white};
  border: none;
  cursor: pointer;
  box-shadow:
    0px 1px 2px rgba(0, 0, 0, 0.06),
    0px 3px 9px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: ${theme.color.grey1};
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing.large}px;
  background-color: ${theme.color.beigeLight};
`

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 890px;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  margin-bottom: ${theme.spacing.mediumLarge}px;
`

const InputLabel = styled(BodySmallMedium)`
  color: ${theme.color.black};
`

const MessageTextarea = styled.textarea`
  padding: 13px ${theme.spacing.medium}px;
  border-radius: ${theme.spacing.medium}px;
  border: none;
  background-color: ${theme.color.white};
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.small}px;
  line-height: 22px;
  color: ${theme.color.black};
  min-height: 182px;
  resize: vertical;

  &::placeholder {
    color: ${theme.color.grey4};
  }

  &:focus {
    outline: none;
  }
`

const SubmitSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.small}px;
`

const SendButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: ${theme.spacing.medium}px ${theme.spacing.mediumLarge}px;
  border-radius: 50px;
  border: none;
  background-color: ${({ $disabled }) => ($disabled ? theme.color.grey2 : theme.color.plum)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: ${theme.font.body};
  font-weight: 500;
  font-size: ${theme.fontSize.default}px;
  line-height: 24px;
  color: ${({ $disabled }) => ($disabled ? theme.color.grey4 : theme.color.white)};

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? theme.color.grey2 : theme.color.plum)};
  }
`

const HelperText = styled(BodyLarge)`
  color: ${theme.color.plum};
  text-align: center;
`

const ErrorText = styled(BodyLarge)`
  color: ${theme.color.red};
  text-align: center;
`
